import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Profissional ativo (para usuários com múltiplas especialidades / "chapéus")
  const [profissionalAtivoId, setProfissionalAtivoIdState] = useState(() => {
    try { return localStorage.getItem('profissionalAtivoId') || null; } catch { return null; }
  });

  const setProfissionalAtivoId = useCallback((id) => {
    setProfissionalAtivoIdState(id);
    try {
      if (id) localStorage.setItem('profissionalAtivoId', id);
      else localStorage.removeItem('profissionalAtivoId');
    } catch { /* noop */ }
  }, []);

  // 2026-08-31 — Ref da última lista COMPLETA de chapéus vista pelo AuthContext.
  // Usada para detectar quando `user.profissionais` chega parcial durante um
  // refresh de token (onAuthStateChange dispara fetchUserData que pode
  // temporariamente devolver menos itens que o real). Sem essa guarda, o
  // useEffect abaixo resetava o chapéu ativo para o `profs[0]` toda vez que
  // isso acontecia — bug do reporte da Dayene Pitelli, que emitia documentos
  // em Musicoterapia e o sistema forçava para Psicologia após qualquer
  // re-renderização do user.
  const ultimosChapeusRef = useRef([]);

  // Sincronizar profissional ativo com user.profissionais
  useEffect(() => {
    // 2026-08-31 — Guarda contra "limpar profissional durante o load da sessão".
    // Quando o usuário faz F5 (ou o Sidebar força `window.location.reload()`
    // depois de trocar de chapéu), o AuthContext monta com `user=null` e vai
    // levar 100-500ms até `initAuth` popular `user`. Se limpássemos o
    // `profissionalAtivoId` só porque `user` momentaneamente é null, o valor
    // salvo no localStorage sumiria e o hook subsequente resetaria para
    // `profs[0]` — exatamente o bug do reporte da Dayene (Musicoterapia
    // voltando para Psicologia após qualquer reload). Só limpamos quando
    // temos certeza que a sessão terminou (`loading=false` + `user=null`).
    if (loading) return;
    if (!user) {
      // Sessão terminou de fato: agora sim limpa.
      if (profissionalAtivoId) setProfissionalAtivoId(null);
      ultimosChapeusRef.current = [];
      return;
    }
    if (user.perfil !== 'profissional') {
      if (profissionalAtivoId) setProfissionalAtivoId(null);
      ultimosChapeusRef.current = [];
      return;
    }
    const profs = user.profissionais || (user.profissional ? [user.profissional] : []);
    // Lista vazia = estado transitório (refresh, race). NÃO tocar em nada.
    if (!profs.length) return;

    const idsAtuais = profs.map(p => p.id);
    const ultimaLista = ultimosChapeusRef.current;

    // Se a lista atual TEM MENOS chapéus do que a última vista, é um refresh
    // parcial — mantém o chapéu selecionado e NÃO atualiza a referência para
    // não "aceitar" o encolhimento como estado válido.
    const encolheu = ultimaLista.length > 0 && idsAtuais.length < ultimaLista.length;
    if (encolheu) {
      console.warn(
        '[AuthContext] user.profissionais chegou incompleto — mantendo chapéu ativo.',
        { antes: ultimaLista, agora: idsAtuais, ativo: profissionalAtivoId }
      );
      return;
    }
    ultimosChapeusRef.current = idsAtuais;

    // Primeira vez sem seleção: adotar o default (profs[0])
    if (!profissionalAtivoId) {
      setProfissionalAtivoId(profs[0].id);
      return;
    }
    // Seleção ainda pertence ao usuário: MANTER
    if (idsAtuais.includes(profissionalAtivoId)) return;
    // Seleção sumiu genuinamente (usuário perdeu esse chapéu): resetar
    console.info(
      '[AuthContext] chapéu ativo não pertence mais ao usuário — resetando para',
      profs[0].id
    );
    setProfissionalAtivoId(profs[0].id);
  }, [user, profissionalAtivoId, setProfissionalAtivoId, loading]);

  // Função para limpar erro (usada na página de login)
  const clearError = () => setError(null);

  // Buscar dados do usuário no backend
  const fetchUserData = useCallback(async (accessToken) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        timeout: 10000
      });
      return response.data;
    } catch (err) {
      console.error('Erro ao buscar dados do usuário:', err);
      
      // Se for erro de autenticação, limpar sessão silenciosamente (sem setar error)
      if (err.response?.status === 401 || err.response?.status === 404) {
        console.log('Sessão inválida, fazendo logout silencioso...');
        try {
          await supabase.auth.signOut();
          // Limpar localStorage relacionado ao Supabase
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.includes('supabase') || key.includes('sb-'))) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
        } catch (e) {
          console.warn('Erro ao fazer signOut:', e);
        }
      }
      
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    
    // Verificar sessão existente
    const initAuth = async () => {
      try {
        const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.warn('Error getting session:', sessionError.message);
          // Limpar sessão inválida SILENCIOSAMENTE (não setar error)
          await supabase.auth.signOut();
          // Limpar localStorage
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.includes('supabase') || key.includes('sb-'))) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
          if (mounted) {
            setLoading(false);
          }
          return;
        }
        
        if (currentSession && mounted) {
          setSession(currentSession);
          const userData = await fetchUserData(currentSession.access_token);
          if (userData && mounted) {
            setUser(userData);
          } else if (mounted) {
            // Se não conseguiu buscar dados do usuário, a sessão pode estar inválida
            console.log('Não foi possível buscar dados do usuário, limpando sessão silenciosamente...');
            setSession(null);
            setUser(null);
            // NÃO setar error aqui - o usuário está sendo redirecionado para login
          }
        }
      } catch (err) {
        console.error('Erro ao inicializar auth:', err);
        // Tentar limpar sessão problemática SILENCIOSAMENTE
        try {
          await supabase.auth.signOut();
          // Limpar localStorage
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.includes('supabase') || key.includes('sb-'))) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
        } catch (e) {
          console.warn('Erro ao limpar sessão:', e);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    // Escutar mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('Auth state change:', event, newSession ? 'has session' : 'no session');
        
        if (!mounted) return;
        
        // Ignorar eventos de INITIAL_SESSION pois já tratamos no initAuth
        if (event === 'INITIAL_SESSION') return;
        
        setSession(newSession);
        
        if (newSession) {
          // Usar setTimeout para evitar condição de corrida
          setTimeout(async () => {
            if (!mounted) return;
            const userData = await fetchUserData(newSession.access_token);
            if (userData && mounted) {
              setUser(userData);
            }
            setLoading(false);
          }, 100);
        } else {
          setUser(null);
          setLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [fetchUserData]);

  // Login com email e senha via Supabase client
  const signIn = async (email, password) => {
    setError(null);
    setLoading(true);
    
    try {
      // SEMPRE limpar qualquer sessão antiga antes de fazer novo login
      try {
        await supabase.auth.signOut();
        // Limpar localStorage relacionado ao Supabase
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes('supabase') || key.includes('sb-'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
      } catch (cleanupErr) {
        console.log('Cleanup antes do login:', cleanupErr.message);
      }
      
      // Fazer login via BACKEND (para registrar audit log)
      const loginResponse = await axios.post(`${API}/auth/login`, {
        email,
        password
      }, { timeout: 15000 });
      
      const { access_token, user: userData } = loginResponse.data;
      
      if (!access_token) {
        throw new Error('Falha ao obter token de acesso');
      }
      
      console.log('Login successful via backend, token:', access_token.substring(0, 50) + '...');
      
      // Agora fazer login no Supabase client para manter sessão sincronizada
      const { data: supaData, error: supaError } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (supaError) {
        console.warn('Supabase client login warning:', supaError.message);
      }
      
      const supaSession = supaData?.session || { access_token };
      
      console.log('User data received:', userData.nome, userData.perfil);
      
      setSession(supaSession);
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (err) {
      console.error('Login error:', err);
      let message = err.response?.data?.detail || err.message || 'Email ou senha incorretos';
      // Simplificar mensagens de erro para o usuário
      if (message.includes('body stream') || message.includes('session') || message.includes('Session')) {
        message = 'Erro de conexão. Por favor, tente novamente.';
      }
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const signOut = async () => {
    // Fix 2026-08 (Fátima Sperafico): antes de deslogar, remove a push
    // subscription DESTE navegador. Sem isso, o backend continua enviando
    // notificações do usuário anterior para este dispositivo mesmo após
    // logout — caso típico de admin testando login em outra conta.
    try {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        const registration = await navigator.serviceWorker.getRegistration();
        const subscription = await registration?.pushManager.getSubscription();
        if (subscription) {
          const endpoint = subscription.endpoint;
          // 1. Remove do backend (usa token ainda válido)
          try {
            const token = session?.access_token;
            if (token) {
              await axios.post(
                `${API}/push/unsubscribe`,
                { endpoint },
                { headers: { Authorization: `Bearer ${token}` } }
              );
            }
          } catch (unsubErr) {
            console.warn('Falha ao remover push subscription no backend:', unsubErr);
          }
          // 2. Cancela do service worker do navegador
          try {
            await subscription.unsubscribe();
          } catch (unsubBrowserErr) {
            console.warn('Falha ao cancelar push subscription local:', unsubBrowserErr);
          }
        }
      }
    } catch (pushErr) {
      console.warn('Erro ao limpar push subscription no logout:', pushErr);
      // Falha aqui NÃO deve impedir o logout — segue.
    }

    try {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      return { success: true };
    } catch (err) {
      console.error('Erro ao fazer logout:', err);
      // Força limpar estado mesmo em erro
      setUser(null);
      setSession(null);
      return { success: true };
    }
  };

  // Helper para fazer requisições autenticadas
  const authFetch = useCallback(async (url, options = {}) => {
    const { suppressAuthRedirect, ...restOptions } = options;
    
    // Token da sessão em cache (evita chamar supabase.auth.getSession() a cada request)
    let token = session?.access_token;
    const expiresAt = session?.expires_at; // Unix seconds
    const nowSec = Math.floor(Date.now() / 1000);
    
    // Só consulta o Supabase se: (1) não temos token, ou (2) token expira em <2 min (refresh preventivo)
    const needsFreshSession = !token || (expiresAt && expiresAt - nowSec < 120);
    
    if (needsFreshSession) {
      try {
        const { data: { session: freshSession } } = await supabase.auth.getSession();
        if (freshSession?.access_token) {
          token = freshSession.access_token;
        }
      } catch (refreshErr) {
        console.warn('[authFetch] Erro ao verificar sessão:', refreshErr);
        // Continuar com o token existente
      }
    }
    
    if (!token) {
      throw new Error('Usuário não autenticado');
    }
    
    try {
      // Header extra: profissional ativo (para usuários com múltiplos "chapéus")
      const extraHeaders = {};
      if (profissionalAtivoId) {
        extraHeaders['X-Profissional-Id'] = profissionalAtivoId;
      }
      
      return await axios({
        url: `${API}${url}`,
        ...restOptions,
        headers: {
          ...restOptions.headers,
          ...extraHeaders,
          Authorization: `Bearer ${token}`
        }
      });
    } catch (err) {
      // Se receber 401 (token inválido/expirado), fazer logout automático SEM setar error
      if (err.response?.status === 401 && !suppressAuthRedirect) {
        console.warn('[authFetch] Token expirado ou inválido. Fazendo logout automático...');

        // Toast amigável avisando o usuário antes do redirect (evita sensação de "travou")
        toast.error('Sua sessão expirou. Redirecionando para o login...', {
          duration: 2500,
          id: 'session-expired', // evita múltiplos toasts em paralelo
        });

        await supabase.auth.signOut();
        setUser(null);
        setSession(null);
        // Limpar localStorage
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes('supabase') || key.includes('sb-'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        // Pequeno delay para o usuário ler o toast antes de cair no login
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        // Throw error sem mensagem que aparece no alert
        throw new Error('SILENT_AUTH_REDIRECT');
      }
      throw err;
    }
  }, [session, profissionalAtivoId]);

  // Verificar se usuário tem permissão para determinados perfis
  const hasPermission = useCallback((perfisPermitidos) => {
    if (!user) return false;
    return perfisPermitidos.includes(user.perfil);
  }, [user]);

  // Verificar se é admin
  const isAdmin = useCallback(() => {
    return user?.perfil === 'administrador';
  }, [user]);

  // Verificar se é recepção
  const isRecepcao = useCallback(() => {
    return user?.perfil === 'recepcao';
  }, [user]);

  // Verificar se é admin ou recepção
  const isAdminOrRecepcao = useCallback(() => {
    return ['administrador', 'recepcao'].includes(user?.perfil);
  }, [user]);

  // Verificar se é profissional
  const isProfissional = useCallback(() => {
    return user?.perfil === 'profissional';
  }, [user]);

  // Recuperação de senha
  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      
      if (error) {
        throw error;
      }
      
      return { success: true };
    } catch (err) {
      console.error('Erro ao enviar email de recuperação:', err);
      return { 
        success: false, 
        error: err.message || 'Erro ao enviar email de recuperação' 
      };
    }
  };

  const value = {
    user,
    session,
    loading,
    error,
    clearError,
    signIn,
    signOut,
    resetPassword,
    authFetch,
    hasPermission,
    isAdmin,
    isRecepcao,
    isAdminOrRecepcao,
    isProfissional,
    isAuthenticated: !!session,
    // Múltiplos "chapéus" (especialidades) do profissional
    profissionalAtivoId,
    setProfissionalAtivoId,
    // WebAuthn functions
    registerBiometric: async (deviceName = 'Meu Dispositivo') => {
      try {
        // 1. Obter challenge do servidor
        const challengeRes = await authFetch('/auth/webauthn/challenge');
        const { challenge, user: userInfo, rp } = challengeRes.data;
        
        // 2. Criar credencial WebAuthn
        const publicKeyCredentialCreationOptions = {
          challenge: Uint8Array.from(atob(challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)),
          rp: {
            name: rp.name,
            id: window.location.hostname
          },
          user: {
            id: Uint8Array.from(userInfo.id, c => c.charCodeAt(0)),
            name: userInfo.name,
            displayName: userInfo.displayName
          },
          pubKeyCredParams: [
            { alg: -7, type: 'public-key' },  // ES256
            { alg: -257, type: 'public-key' } // RS256
          ],
          authenticatorSelection: {
            authenticatorAttachment: 'platform',
            userVerification: 'required',
            residentKey: 'preferred'
          },
          timeout: 60000,
          attestation: 'none'
        };
        
        // 3. Solicitar criação de credencial (mostra prompt biométrico)
        const credential = await navigator.credentials.create({
          publicKey: publicKeyCredentialCreationOptions
        });
        
        // 4. Enviar credencial para o servidor
        const credentialId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)))
          .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
        const publicKey = btoa(String.fromCharCode(...new Uint8Array(credential.response.getPublicKey())))
          .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
        
        await authFetch('/auth/webauthn/register', {
          method: 'POST',
          data: {
            credential_id: credentialId,
            public_key: publicKey,
            device_name: deviceName
          }
        });
        
        return { success: true };
      } catch (err) {
        console.error('Error registering biometric:', err);
        if (err.name === 'NotAllowedError') {
          return { success: false, error: 'Operação cancelada pelo usuário' };
        }
        return { success: false, error: err.message || 'Erro ao registrar biometria' };
      }
    },
    getBiometricCredentials: async () => {
      try {
        const res = await authFetch('/auth/webauthn/my-credentials');
        return res.data.credentials || [];
      } catch (err) {
        return [];
      }
    },
    removeBiometricCredential: async (credentialId) => {
      try {
        await authFetch(`/auth/webauthn/credential/${credentialId}`, { method: 'DELETE' });
        return { success: true };
      } catch (err) {
        return { success: false, error: err.message };
      }
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
